import { FC, FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { isAxiosError } from 'axios'
import { PencilIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-toastify'
import { Button } from 'src/components/button'
import { Input } from 'src/components/input'
import { ErrorsList } from 'src/components/errors'
import {
  AddressAutocompleteField,
  type StructuredAddress,
} from 'src/components/shared/address-autocomplete-field'
import { propertyApi } from 'src/services/properties/api'
import { propertyQueries } from 'src/services/properties/queries'
import type { Property } from 'src/services/properties/models'
import {
  EditPropertyForm,
  EditPropertyFormInputs,
  TEditPropertyForm,
} from 'src/services/properties/forms'
import { InfoHint } from 'src/components/info-hint'

type PropertyBasicInfoCardProps = {
  property: Property
}

/** Parse a numeric form field value; empty string / NaN -> undefined. */
const parseCount = (raw: string | undefined): number | null => {
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

const parseDecimal = (raw: string | undefined): number | null => {
  if (!raw) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

/**
 * Server sends a local time-of-day as "HH:MM:SS"; <input type="time"> wants
 * "HH:MM". Pure string slice — never route a time-of-day through Date, which
 * would apply the browser's timezone.
 */
export const toTimeInputValue = (raw: string | null | undefined): string =>
  raw ? raw.slice(0, 5) : ''

const SUPPORTED_TIMEZONES: string[] = Intl.supportedValuesOf('timeZone')

/** IANA zones the browser knows, plus the stored one if the browser's list lacks it. */
const timezoneOptions = (current: string | undefined): string[] =>
  current && !SUPPORTED_TIMEZONES.includes(current)
    ? [current, ...SUPPORTED_TIMEZONES]
    : SUPPORTED_TIMEZONES

const InfoRow = ({
  label,
  value,
}: {
  label: string
  value: string | number | null | undefined
}) => (
  <div>
    <dt className="text-xs font-medium text-text-placeholder">{label}</dt>
    <dd className="mt-0.5 text-sm text-text-primary">{value ?? '\u2014'}</dd>
  </div>
)

const PropertyBasicInfoCardInner: FC<PropertyBasicInfoCardProps> = ({ property }) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const { form, createFormFieldChangeHandler, validate, overrideForm } =
    useTnForm<TEditPropertyForm>()

  // Seed form with current property values when entering edit mode.
  useEffect(() => {
    if (isEditing) {
      const seeded = EditPropertyForm.create({
        _name: property.name ?? '',
        description: property.description ?? '',
        street: property.street ?? '',
        city: property.city ?? '',
        stateOrRegion: property.stateOrRegion ?? '',
        country: property.country ?? '',
        postalCode: property.postalCode ?? '',
        latitude: property.latitude != null ? String(property.latitude) : '',
        longitude: property.longitude != null ? String(property.longitude) : '',
        placeId: property.placeId ?? '',
        bedroomCnt: property.bedroomCnt != null ? String(property.bedroomCnt) : '',
        bathroomCnt: property.bathroomCnt != null ? String(property.bathroomCnt) : '',
        checkInTime: toTimeInputValue(property.checkInTime),
        checkOutTime: toTimeInputValue(property.checkOutTime),
        timezone: property.timezone ?? '',
      })
      overrideForm(seeded as unknown as TEditPropertyForm)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing])

  // Build the current StructuredAddress from form fields for the autocomplete component.
  const addressValue = useMemo((): StructuredAddress | null => {
    const hasAnyField =
      form.street.value || form.city.value || form.stateOrRegion.value || form.postalCode.value
    if (!hasAnyField) return null
    return {
      streetAddress: form.street.value || undefined,
      city: form.city.value || undefined,
      state: form.stateOrRegion.value || undefined,
      postalCode: form.postalCode.value || undefined,
      country: form.country.value || undefined,
      latitude: parseDecimal(form.latitude.value),
      longitude: parseDecimal(form.longitude.value),
      placeId: form.placeId.value || null,
      rawAddress: [
        form.street.value,
        form.city.value,
        form.stateOrRegion.value,
        form.postalCode.value,
      ]
        .filter(Boolean)
        .join(', '),
    }
  }, [
    form.street.value,
    form.city.value,
    form.stateOrRegion.value,
    form.postalCode.value,
    form.country.value,
    form.latitude.value,
    form.longitude.value,
    form.placeId.value,
  ])

  const handleAddressChange = useCallback(
    (addr: StructuredAddress | null) => {
      createFormFieldChangeHandler(form.street)(addr?.streetAddress ?? '')
      createFormFieldChangeHandler(form.city)(addr?.city ?? '')
      createFormFieldChangeHandler(form.stateOrRegion)(addr?.state ?? '')
      createFormFieldChangeHandler(form.postalCode)(addr?.postalCode ?? '')
      createFormFieldChangeHandler(form.latitude)(
        addr?.latitude != null ? String(addr.latitude) : '',
      )
      createFormFieldChangeHandler(form.longitude)(
        addr?.longitude != null ? String(addr.longitude) : '',
      )
      createFormFieldChangeHandler(form.placeId)(addr?.placeId ?? '')
      // A new address re-derives the timezone on save unless the host picks one after.
      createFormFieldChangeHandler(form.timezone)('')

      if (addr?.country) {
        createFormFieldChangeHandler(form.country)(addr.country.toUpperCase())
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createFormFieldChangeHandler],
  )

  const { mutate: saveProperty, isPending } = useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      propertyApi.update({ id: property.id, ...input } as never),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...propertyQueries.all(), property.id] })
      toast.success(t('propertyForm.updatedToast'))
      setIsEditing(false)
    },
    onError: (error: unknown) => {
      if (isAxiosError(error) && error.response?.status === 400 && error.response.data) {
        const data = error.response.data as Record<string, string[] | string>
        const serverToForm: Record<string, keyof EditPropertyFormInputs> = {
          name: '_name',
          description: 'description',
          street: 'street',
          city: 'city',
          state_or_region: 'stateOrRegion',
          country: 'country',
          postal_code: 'postalCode',
          bedroom_cnt: 'bedroomCnt',
          bathroom_cnt: 'bathroomCnt',
          check_in_time: 'checkInTime',
          check_out_time: 'checkOutTime',
          timezone: 'timezone',
        }
        let matched = false
        for (const [serverKey, formKey] of Object.entries(serverToForm)) {
          const raw = data[serverKey]
          if (!raw) continue
          matched = true
          const messages = Array.isArray(raw) ? raw : [raw]
          ;(form[formKey] as { errors: { code: string; message: string }[] }).errors = messages.map(
            (message, idx) => ({
              code: `server-${serverKey}-${idx}`,
              message,
            }),
          )
        }
        if (matched) {
          overrideForm(form)
          return
        }
      }
      toast.error(t('propertyForm.updateError'))
    },
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    validate()
    if (!form.isValid) return

    const input: Record<string, unknown> = {
      name: form._name.value ?? '',
      description: form.description.value || null,
      street: form.street.value || null,
      city: form.city.value || null,
      stateOrRegion: form.stateOrRegion.value || null,
      country: form.country.value || null,
      postalCode: form.postalCode.value || null,
      latitude: parseDecimal(form.latitude.value),
      longitude: parseDecimal(form.longitude.value),
      placeId: form.placeId.value || null,
      bedroomCnt: parseCount(form.bedroomCnt.value),
      bathroomCnt: parseCount(form.bathroomCnt.value),
      timezone: form.timezone.value || null,
    }
    // Times are non-nullable on the server; omit a cleared input instead of sending null.
    if (form.checkInTime.value) input.checkInTime = form.checkInTime.value
    if (form.checkOutTime.value) input.checkOutTime = form.checkOutTime.value
    saveProperty(input)
  }

  const handleCancel = () => {
    setIsEditing(false)
  }

  if (!isEditing) {
    return (
      <div className="rounded-lg border border-border bg-surface p-4">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-medium text-text-primary">{t('propertyForm.basicInfo')}</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconOnly
            tooltip={t('propertyForm.editBasicInfo')}
            onClick={() => setIsEditing(true)}
          >
            <PencilIcon className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
          <div className="col-span-2">
            <InfoRow label={t('propertyForm.labelName')} value={property.name} />
            <InfoRow label={t('propertyForm.labelDescription')} value={property.description} />
          </div>
          <InfoRow label={t('propertyForm.labelStreet')} value={property.street} />
          <InfoRow label={t('propertyForm.labelCity')} value={property.city} />
          <InfoRow label={t('propertyForm.labelState')} value={property.stateOrRegion} />
          <InfoRow label={t('propertyForm.labelCountry')} value={property.country} />
          <InfoRow label={t('propertyForm.labelPostalCode')} value={property.postalCode} />
          <InfoRow label={t('propertyForm.labelBedrooms')} value={property.bedroomCnt} />
          <InfoRow label={t('propertyForm.labelBathrooms')} value={property.bathroomCnt} />
          <InfoRow
            label={t('propertyForm.labelCheckInTime')}
            value={toTimeInputValue(property.checkInTime) || null}
          />
          <InfoRow
            label={t('propertyForm.labelCheckOutTime')}
            value={toTimeInputValue(property.checkOutTime) || null}
          />
          <InfoRow
            label={t('propertyForm.labelTimezone')}
            value={property.timezone ?? t('propertyForm.notAvailable')}
          />
        </dl>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <h4 className="mb-3 text-sm font-medium text-text-primary">
        {t('propertyForm.editBasicInfo')}
      </h4>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <Input
            autoFocus
            label={form._name.label}
            placeholder={form._name.placeholder}
            value={form._name.value ?? ''}
            onChange={(e) => createFormFieldChangeHandler(form._name)(e.target.value)}
            onBlur={() => form._name.validate()}
          />
          <ErrorsList errors={form._name.errors} />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-primary">
            {form.description.label}
          </label>
          <textarea
            className="w-full rounded-md border bg-surface p-2 text-text-primary placeholder:font-thin placeholder:text-text-placeholder focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-strong"
            placeholder={form.description.placeholder}
            value={form.description.value ?? ''}
            onChange={(e) => createFormFieldChangeHandler(form.description)(e.target.value)}
            rows={3}
          />
          <ErrorsList errors={form.description.errors} />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-primary">
            {t('propertyForm.address')}
          </label>
          <AddressAutocompleteField
            value={addressValue}
            onChange={handleAddressChange}
            disabled={isPending}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Input
              type="number"
              label={form.bedroomCnt.label}
              placeholder={form.bedroomCnt.placeholder}
              value={form.bedroomCnt.value ?? ''}
              onChange={(e) => createFormFieldChangeHandler(form.bedroomCnt)(e.target.value)}
            />
          </div>
          <div>
            <Input
              type="number"
              label={form.bathroomCnt.label}
              placeholder={form.bathroomCnt.placeholder}
              value={form.bathroomCnt.value ?? ''}
              onChange={(e) => createFormFieldChangeHandler(form.bathroomCnt)(e.target.value)}
            />
          </div>
        </div>

        <div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Input
                type="time"
                label={t('propertyForm.labelCheckInTime')}
                hint={t('propertyForm.stayTimesHelp')}
                value={form.checkInTime.value ?? ''}
                onChange={(e) => createFormFieldChangeHandler(form.checkInTime)(e.target.value)}
                data-testid="check-in-time"
              />
              <ErrorsList errors={form.checkInTime.errors} />
            </div>
            <div>
              <Input
                type="time"
                label={t('propertyForm.labelCheckOutTime')}
                value={form.checkOutTime.value ?? ''}
                onChange={(e) => createFormFieldChangeHandler(form.checkOutTime)(e.target.value)}
                data-testid="check-out-time"
              />
              <ErrorsList errors={form.checkOutTime.errors} />
            </div>
          </div>
        </div>

        <div>
          <label
            htmlFor="property-timezone"
            className="mb-1 block text-sm font-medium text-primary"
          >
            {t('propertyForm.labelTimezone')}
            <InfoHint text={t('propertyForm.timezoneHelp')} className="ml-1.5" />
          </label>
          <select
            id="property-timezone"
            value={form.timezone.value ?? ''}
            onChange={(e) => createFormFieldChangeHandler(form.timezone)(e.target.value)}
            className="w-full rounded-md border bg-surface p-2 font-normal text-text-primary focus:outline-none focus:ring-2 focus:ring-border-strong"
            data-testid="property-timezone"
          >
            <option value="">{t('propertyForm.timezoneAuto')}</option>
            {timezoneOptions(form.timezone.value).map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
          <ErrorsList errors={form.timezone.errors} />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={handleCancel}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={isPending}>
            {isPending ? t('propertyForm.savingBtn') : t('common.save')}
          </Button>
        </div>
      </form>
    </div>
  )
}

export const PropertyBasicInfoCard: FC<PropertyBasicInfoCardProps> = (props) => {
  return (
    <FormProvider<EditPropertyFormInputs> formClass={EditPropertyForm} key={props.property.id}>
      <PropertyBasicInfoCardInner {...props} />
    </FormProvider>
  )
}
